/**
 * AI Product Mapping — row-review mutations (confirm / edit / mark-no-match and
 * their bulk counterparts), split out of the run slice to keep it within the
 * repo's file-size budget. Each function mutates the passed rows in place —
 * they run inside the slice's immer reducers.
 */

import { ROW_STATUS } from './constants';
import {
  bulkConfirmTargets,
  bulkMarkNoMatchTargets,
  isBulkConfirmUndo,
  isBulkMarkNoMatchUndo,
  topCandidate,
} from './matchPolicy';
import type { GridRow } from './types';

export interface ConfirmRowPayload {
  idx: number;
  sku?: string;
  matchedTitle?: string | null;
}

export interface EditRowPayload {
  idx: number;
  sku: string;
  matchedTitle?: string | null;
}

export interface EditRowsPayload {
  idxs: number[];
  sku: string;
  matchedTitle?: string | null;
}

// The pre-acceptance snapshot taken before a best guess is committed, and its
// restore — shared by the single-row and bulk confirm paths so the saved field
// set can only ever change in one place.
function snapshotPreConfirm(row: GridRow): void {
  row.preConfirm = {
    status: row.status,
    confidence: row.confidence,
    matchedSku: row.matchedSku,
    matchedTitle: row.matchedTitle,
  };
}

function restorePreConfirm(row: GridRow): void {
  if (!row.preConfirm) return;
  row.status = row.preConfirm.status;
  row.confidence = row.preConfirm.confidence;
  row.matchedSku = row.preConfirm.matchedSku;
  row.matchedTitle = row.preConfirm.matchedTitle;
  row.preConfirm = undefined;
}

export function confirmRow(rows: GridRow[], payload: ConfirmRowPayload): void {
  const row = rows[payload.idx - 1];
  if (!row) return;
  if (payload.sku) {
    // Accept a surfaced best guess (no-match / policy-promoted row, via "use top suggestion"):
    // commit the SKU as a confirmed match. Snapshot the pre-acceptance state first so
    // un-confirming restores it (incl. the original confidence). Always confirms — an accept.
    snapshotPreConfirm(row);
    row.confirmed = true;
    row.edited = false;
    row.markedNoMatch = false;
    row.matchedSku = payload.sku;
    row.matchedTitle = payload.matchedTitle ?? null;
    row.status = ROW_STATUS.HI;
    row.confidence = 1;
    return;
  }
  // Plain confirm on a committed match toggles: a second click un-confirms. If the row was an
  // accepted best guess, restore its pre-acceptance state (status + confidence) so undo is a
  // true revert; otherwise just drop the confirmed flag. Confirming clears any prior edit/gap.
  row.confirmed = !row.confirmed;
  if (row.confirmed) {
    row.edited = false;
    row.markedNoMatch = false;
  } else {
    restorePreConfirm(row);
  }
}

export function editRow(rows: GridRow[], payload: EditRowPayload): void {
  const row = rows[payload.idx - 1];
  if (row) {
    row.edited = true;
    row.confirmed = false;
    row.markedNoMatch = false;
    row.preConfirm = undefined;
    row.matchedSku = payload.sku;
    row.matchedTitle = payload.matchedTitle ?? null;
    row.status = ROW_STATUS.HI;
    row.confidence = 1;
  }
}

// Mark a row as a genuine no-match (catalog gap). Non-destructive: the match data
// (matchedSku, candidates, status) is kept so the editor still shows the AI suggestions and
// the user can revert. The policy view projects it to a no-match for the grid; counts + export
// treat it as one. The negative counterpart of confirmRow; clears any prior confirm/edit.
export function markNoMatch(rows: GridRow[], payload: { idx: number }): void {
  const row = rows[payload.idx - 1];
  if (!row) return;
  row.markedNoMatch = !row.markedNoMatch;
  if (row.markedNoMatch) {
    row.confirmed = false;
    row.edited = false;
    row.preConfirm = undefined;
  }
}

// --- Bulk mutations (selection-driven) ---
// Mostly set semantics, NOT toggles like their single-row cousins: re-applying an
// action to a row that already satisfies it leaves the row unchanged, so a sweep can
// never un-confirm/un-mark strays. The one deliberate exception is Confirm over an
// all-confirmed selection — see confirmRows.

// Confirm: confirm the committed match, or accept a no-match/promoted row's best guess
// (same commit the per-row "use top suggestion" makes). Rows with nothing to confirm
// (in-flight, errored, dead ends) are skipped. When EVERY touchable row is already
// confirmed, the click is an undo of the previous confirm and un-confirms them all —
// restoring accepted best guesses — mirroring the per-row confirm toggle.
export function confirmRows(rows: GridRow[], idxs: number[]): void {
  const targets = bulkConfirmTargets(rows, idxs);
  if (isBulkConfirmUndo(targets)) {
    for (const row of targets) {
      row.confirmed = false;
      restorePreConfirm(row);
    }
    return;
  }
  for (const row of targets) {
    if (row.confirmed) continue;
    if (!row.matchedSku) {
      const top = topCandidate(row);
      if (!top) continue; // targets excludes dead ends; guard for safety
      snapshotPreConfirm(row);
      row.matchedSku = top.sku;
      row.matchedTitle = top.title;
      row.status = ROW_STATUS.HI;
      row.confidence = 1;
    }
    row.confirmed = true;
    row.edited = false;
    row.markedNoMatch = false;
  }
}

// Same undo rule as confirmRows: an ALL-marked selection un-marks (the undo of the
// previous bulk mark); otherwise set semantics — already-marked rows stay marked.
// Un-marking only clears the flag, like the per-row toggle: the mark was
// non-destructive, so the row's match data is still in place.
export function markNoMatchRows(rows: GridRow[], idxs: number[]): void {
  const targets = bulkMarkNoMatchTargets(rows, idxs);
  if (isBulkMarkNoMatchUndo(targets)) {
    for (const row of targets) row.markedNoMatch = false;
    return;
  }
  for (const row of targets) {
    if (row.markedNoMatch) continue;
    row.markedNoMatch = true;
    row.confirmed = false;
    row.edited = false;
    row.preConfirm = undefined;
  }
}

// Apply one picked SKU to every selected row — the bulk counterpart of editRow.
export function editRows(rows: GridRow[], payload: EditRowsPayload): void {
  for (const idx of payload.idxs) {
    const row = rows[idx - 1];
    if (!row || row.status === ROW_STATUS.PENDING || row.status === ROW_STATUS.PROCESSING) continue;
    row.edited = true;
    row.confirmed = false;
    row.markedNoMatch = false;
    row.preConfirm = undefined;
    row.matchedSku = payload.sku;
    row.matchedTitle = payload.matchedTitle ?? null;
    row.status = ROW_STATUS.HI;
    row.confidence = 1;
  }
}
